import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
  Textarea,
  useDisclosure
} from '@nextui-org/react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { applySort } from '../lib/sort';
import { useAuth } from '../hooks/useAuth';

const PAGE_LIMIT = 2000;

const REGION_OPTIONS = [
  { key: 'all', label: 'Todas' },
  { key: 'EU', label: 'Europa' },
  { key: 'US', label: 'Estados Unidos' },
  { key: 'LATAM', label: 'Latinoamérica' },
  { key: 'BR', label: 'Brasil' },
  { key: 'ASIA', label: 'Asia' },
  { key: 'ME', label: 'Oriente Medio' },
  { key: 'AF', label: 'África' },
  { key: 'OC', label: 'Oceanía' }
];

function tsToDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function tsToInputValue(value) {
  const d = tsToDate(value);
  if (!d) return '';
  return d.toISOString().slice(0, 10);
}

function formatDate(value) {
  const d = tsToDate(value);
  return d ? d.toLocaleDateString() : '—';
}

function isPast(value) {
  const d = tsToDate(value);
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

// Admin escribe el name/description con HTML enriquecido desde la app Electron.
// Aquí renderizamos tal cual (admin-only input → XSS tolerable) y ofrecemos
// edición en texto plano para nuevos eventos.
function stripHtml(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

const EMPTY_FORM = {
  name: '',
  description: '',
  icon: '🎯',
  officialUrl: '',
  startDate: '',
  endDate: '',
  targetRegion: 'all',
  active: true
};

export default function Eventos() {
  const { canWrite } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('active');
  const [toast, setToast] = useState(null);
  const [sortDescriptor, setSortDescriptor] = useState({
    column: 'startDate',
    direction: 'descending'
  });

  const detail = useDisclosure();
  const form = useDisclosure();
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formId, setFormId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Suscripción en tiempo real a tiktok_events.
  // orderBy('__name__') para no excluir eventos sin startDate (Firestore
  // omite docs que no tengan el campo del orderBy). El sort por fecha lo
  // hace applySort en cliente.
  useEffect(() => {
    setLoading(true);
    setError(null);
    const q = query(
      collection(db, 'tiktok_events'),
      orderBy('__name__'),
      fsLimit(PAGE_LIMIT)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('[Eventos] onSnapshot error:', err);
        setError(err.message || 'Error cargando eventos');
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const counts = useMemo(() => {
    const acc = { all: events.length, active: 0, history: 0 };
    for (const ev of events) {
      if (isPast(ev.endDate)) acc.history++;
      else acc.active++;
    }
    return acc;
  }, [events]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = events.filter((ev) => {
      const finished = isPast(ev.endDate);
      if (stateFilter === 'active' && finished) return false;
      if (stateFilter === 'history' && !finished) return false;
      if (!needle) return true;
      const name = stripHtml(ev.name).toLowerCase();
      const desc = stripHtml(ev.description).toLowerCase();
      const region = (ev.targetRegion || '').toLowerCase();
      return (
        name.includes(needle) ||
        desc.includes(needle) ||
        region.includes(needle)
      );
    });

    return applySort(base, sortDescriptor, {
      name: (e) => stripHtml(e.name).toLowerCase(),
      region: (e) => (e.targetRegion || '').toLowerCase(),
      startDate: (e) => {
        const d = tsToDate(e.startDate);
        return d ? d.getTime() : 0;
      },
      endDate: (e) => {
        const d = tsToDate(e.endDate);
        return d ? d.getTime() : 0;
      },
      state: (e) => {
        if (isPast(e.endDate)) return 2; // finalizado al final
        return e.active === false ? 1 : 0; // activo primero
      }
    });
  }, [events, search, stateFilter, sortDescriptor]);

  function openNew() {
    setFormData(EMPTY_FORM);
    setFormId(null);
    form.onOpen();
  }

  function openEdit(ev) {
    setFormData({
      name: stripHtml(ev.name),
      description: stripHtml(ev.description),
      icon: ev.icon || '🎯',
      officialUrl: ev.officialUrl || '',
      startDate: tsToInputValue(ev.startDate),
      endDate: tsToInputValue(ev.endDate),
      targetRegion: ev.targetRegion || 'all',
      active: ev.active !== false
    });
    setFormId(ev.id);
    form.onOpen();
  }

  function openDetail(ev) {
    setSelected(ev);
    detail.onOpen();
  }

  async function saveForm() {
    if (!formData.name.trim()) {
      setToast({ type: 'error', msg: 'El nombre es obligatorio.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        icon: formData.icon.trim() || '🎯',
        officialUrl: formData.officialUrl.trim() || '',
        startDate: formData.startDate ? new Date(formData.startDate) : null,
        endDate: formData.endDate ? new Date(formData.endDate) : null,
        targetRegion: formData.targetRegion || 'all',
        active: formData.active,
        updatedAt: serverTimestamp()
      };

      if (formId) {
        await updateDoc(doc(db, 'tiktok_events', formId), payload);
        setToast({ type: 'ok', msg: 'Evento actualizado.' });
      } else {
        payload.createdAt = serverTimestamp();
        payload.createdBy = auth.currentUser?.uid || null;
        await addDoc(collection(db, 'tiktok_events'), payload);
        setToast({ type: 'ok', msg: 'Evento creado.' });
      }
      form.onClose();
      // No hace falta refresh manual: onSnapshot empuja el cambio automáticamente.
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(ev) {
    try {
      await updateDoc(doc(db, 'tiktok_events', ev.id), {
        active: !ev.active,
        updatedAt: serverTimestamp()
      });
      setEvents((list) =>
        list.map((x) => (x.id === ev.id ? { ...x, active: !ev.active } : x))
      );
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    }
  }

  async function handleDelete(ev) {
    if (!confirm(`¿Eliminar "${stripHtml(ev.name) || ev.id}"? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteDoc(doc(db, 'tiktok_events', ev.id));
      setToast({ type: 'ok', msg: 'Evento eliminado.' });
      setEvents((list) => list.filter((x) => x.id !== ev.id));
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Eventos</h1>
          <div className="flex items-center gap-2 text-sm text-default-500">
            {loading ? (
              'Cargando…'
            ) : (
              <>
                <span>
                  {filtered.length} de {events.length} (tiktok_events)
                </span>
                {events.length >= PAGE_LIMIT && (
                  <Chip size="sm" color="warning" variant="flat">
                    límite {PAGE_LIMIT} alcanzado
                  </Chip>
                )}
              </>
            )}
          </div>
        </div>
        {canWrite && (
          <Button color="primary" onPress={openNew}>
            + Nuevo evento
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Buscar por nombre, descripción o región…"
          value={search}
          onValueChange={setSearch}
          isClearable
          size="sm"
          className="max-w-sm"
        />
        <Tabs
          size="sm"
          selectedKey={stateFilter}
          onSelectionChange={(k) => setStateFilter(String(k))}
          aria-label="Filtrar por estado"
        >
          <Tab key="active" title={`Activos (${counts.active})`} />
          <Tab key="history" title={`Historial (${counts.history})`} />
          <Tab key="all" title={`Todos (${counts.all})`} />
        </Tabs>
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </div>
      )}

      {toast && (
        <div
          className={`rounded-md border p-3 text-sm ${
            toast.type === 'ok'
              ? 'border-success-200 bg-success-50 text-success-700'
              : 'border-danger-200 bg-danger-50 text-danger-700'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <span>{toast.msg}</span>
            <button className="text-xs underline" onClick={() => setToast(null)}>
              cerrar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : (
        <Table
          aria-label="Tabla de eventos TikTok"
          isStriped
          removeWrapper
          classNames={{ th: 'bg-default-100' }}
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
        >
          <TableHeader>
            <TableColumn key="name" allowsSorting>Evento</TableColumn>
            <TableColumn key="region" allowsSorting>Región</TableColumn>
            <TableColumn key="startDate" allowsSorting>Inicio</TableColumn>
            <TableColumn key="endDate" allowsSorting>Fin</TableColumn>
            <TableColumn key="state" allowsSorting>Estado</TableColumn>
            <TableColumn align="end">Acciones</TableColumn>
          </TableHeader>
          <TableBody emptyContent="No hay eventos.">
            {filtered.map((ev) => {
              const finished = isPast(ev.endDate);
              return (
                <TableRow key={ev.id}>
                  <TableCell>
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{ev.icon || '🎯'}</span>
                      <div className="flex flex-col">
                        <span
                          className="font-medium"
                          dangerouslySetInnerHTML={{ __html: ev.name || '—' }}
                        />
                        <span className="text-xs text-default-400">{ev.id}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip size="sm" variant="flat">{ev.targetRegion || 'all'}</Chip>
                  </TableCell>
                  <TableCell>{formatDate(ev.startDate)}</TableCell>
                  <TableCell>{formatDate(ev.endDate)}</TableCell>
                  <TableCell>
                    {finished ? (
                      <Chip size="sm" variant="flat" color="default">Finalizado</Chip>
                    ) : canWrite ? (
                      <Switch
                        size="sm"
                        isSelected={ev.active !== false}
                        onValueChange={() => toggleActive(ev)}
                      >
                        <span className="text-xs">
                          {ev.active !== false ? 'Activo' : 'Inactivo'}
                        </span>
                      </Switch>
                    ) : (
                      <Chip
                        size="sm"
                        variant="flat"
                        color={ev.active !== false ? 'success' : 'default'}
                      >
                        {ev.active !== false ? 'Activo' : 'Inactivo'}
                      </Chip>
                    )}
                  </TableCell>
                  <TableCell>
                    {canWrite ? (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="flat" onPress={() => openDetail(ev)}>
                          Ver
                        </Button>
                        <Button size="sm" variant="flat" onPress={() => openEdit(ev)}>
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="flat"
                          color="danger"
                          onPress={() => handleDelete(ev)}
                        >
                          Eliminar
                        </Button>
                      </div>
                    ) : (
                      <div className="flex justify-end">
                        <span className="text-xs text-default-400">solo lectura</span>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Detalle */}
      <Modal isOpen={detail.isOpen} onClose={detail.onClose} size="3xl" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader>
            <div className="flex flex-col">
              <span dangerouslySetInnerHTML={{ __html: selected?.name || selected?.id || '' }} />
              <span className="text-xs font-normal text-default-400">{selected?.id}</span>
            </div>
          </ModalHeader>
          <ModalBody>
            <pre className="max-h-[60vh] overflow-auto rounded bg-default-100 p-3 text-xs">
              {selected ? JSON.stringify(selected, null, 2) : ''}
            </pre>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={detail.onClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Crear / editar */}
      <Modal
        isOpen={form.isOpen}
        onClose={form.onClose}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>{formId ? 'Editar evento' : 'Nuevo evento'}</ModalHeader>
          <ModalBody className="flex flex-col gap-3">
            <Input
              label="Nombre"
              value={formData.name}
              onValueChange={(v) => setFormData((f) => ({ ...f, name: v }))}
              isRequired
            />
            <Textarea
              label="Descripción"
              value={formData.description}
              onValueChange={(v) => setFormData((f) => ({ ...f, description: v }))}
              minRows={3}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Icono"
                value={formData.icon}
                onValueChange={(v) => setFormData((f) => ({ ...f, icon: v }))}
                description="Un emoji"
              />
              <Input
                label="URL oficial"
                type="url"
                value={formData.officialUrl}
                onValueChange={(v) => setFormData((f) => ({ ...f, officialUrl: v }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Fecha inicio"
                type="date"
                value={formData.startDate}
                onValueChange={(v) => setFormData((f) => ({ ...f, startDate: v }))}
              />
              <Input
                label="Fecha fin"
                type="date"
                value={formData.endDate}
                onValueChange={(v) => setFormData((f) => ({ ...f, endDate: v }))}
              />
            </div>
            <Select
              label="Región destino"
              selectedKeys={[formData.targetRegion]}
              onSelectionChange={(keys) =>
                setFormData((f) => ({ ...f, targetRegion: String(Array.from(keys)[0] || 'all') }))
              }
            >
              {REGION_OPTIONS.map((r) => (
                <SelectItem key={r.key}>{r.label}</SelectItem>
              ))}
            </Select>
            <Switch
              isSelected={formData.active}
              onValueChange={(v) => setFormData((f) => ({ ...f, active: v }))}
            >
              Activo
            </Switch>
            {formId && (
              <p className="text-xs text-default-400">
                Nota: imagen, PDF y vídeo sólo se editan desde la app Electron
                hasta que implementemos los uploads R2 en el backoffice.
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={form.onClose} isDisabled={saving}>
              Cancelar
            </Button>
            <Button color="primary" onPress={saveForm} isLoading={saving}>
              {formId ? 'Guardar' : 'Crear'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
